import { useState } from 'react';

import {
  CONTRACT_TYPES,
  ENDPOINTS,
  PROCESSING_STATUSES,
  REGIONS,
  type AnalyticsOverviewResponse,
  type AnalyticsPeriod,
  type ContractType,
  type Region,
} from '@clm/shared-types';

import { fetchAnalyticsOverview } from '../api/analytics';
import { useCloudeqEntities } from '../hooks/useCloudeqEntities';
import { StatTile } from '../components/charts/StatTile';
import { requireSurface } from '../routes/surfaces';
import { ANALYTICS_FORBIDDEN_COPY, NO_MONEY_COPY, STATUS_LABELS, STATUS_TONES, localDayStartUtc } from './analytics-shared';
import { PanelProblem } from './quality/panel';
import { usePanel } from './quality/panel-state';

/**
 * Analytics **Overview** — CLM-81 (D-81-1…D-81-16; R-81-1).
 *
 * The status board of the reference screenshot, re-mapped onto the states CLM
 * records: one tile per `processing_status` (eight) and one per
 * `contract_type` (five, D-81-12), each with its count and today's intake, over
 * a period (`1w | 1m | 3m | 1y` on `created_utc`, default one month, D-81-14)
 * and an optional Client (the primary party, D-81-8). One read,
 * `GET /v1/analytics/overview`, scoped on the API side: a practice reader sees
 * *their* scope's counts, never a 403, and the Client options are the same
 * response's `clients[]` — so the dropdown lists only clients they can see.
 *
 * ## No money figures (D-81-2)
 *
 * The reference's per-tile currency total is absent on purpose: contract value
 * is stored as the extracted phrase, not a number. The page says so.
 *
 * ## How many are provisional (CLM-110, R-110-23)
 *
 * Each type tile also says how many of its contracts are still provisional —
 * no administrator has confirmed the type — from `by_type_provisional`, under
 * the same filters and scope. A count by type that looked settled would be
 * D-2.1-4's failure applied to the type.
 *
 * ## Type pending is its own tile (CLM-118, B19, R-118-12)
 *
 * A contract with no type yet is stored as `OTHER` plus a pending marker, and
 * the API keeps it out of `by_type.OTHER`: it is counted on its own **Type
 * pending** tile (`by_type_pending`), never as Other.
 *
 * ## "N today" (D-81-9)
 *
 * The boundary is the browser's local midnight expressed in UTC, computed on
 * every fetch and sent as `day_start_utc`; the server never guesses a timezone.
 */

const ENDPOINT_PATH = ENDPOINTS['analytics-overview'].path;
const PANEL = 'Analytics overview';

const TYPE_LABELS: Readonly<Record<ContractType, string>> = {
  MSA: 'MSA',
  NDA: 'NDA',
  SOW: 'SOW',
  AMENDMENT: 'Amendment',
  OTHER: 'Other',
};

const PERIOD_OPTIONS: readonly { readonly value: AnalyticsPeriod; readonly label: string }[] = [
  { value: '1w', label: '1 week' },
  { value: '1m', label: '1 month' },
  { value: '3m', label: '3 months' },
  { value: '1y', label: '1 year' },
];

export function AnalyticsOverview() {
  const surface = requireSurface('analytics-overview');
  const [period, setPeriod] = useState<AnalyticsPeriod>('1m');
  const [partyKey, setPartyKey] = useState<string>('all');
  /* CLM-87 — the PRIMARY party's region; AND-ed beside the client by the API (D-87-11). */
  const [region, setRegion] = useState<'all' | Region>('all');
  /* CLM-91 — the signing cloudEQ entity; the options are the registry rows (D-91-5), loaded once. */
  const [entity, setEntity] = useState<string>('all');
  const entities = useCloudeqEntities();

  const { state, reload } = usePanel<AnalyticsOverviewResponse>(
    (abort) =>
      fetchAnalyticsOverview(
        {
          period,
          ...(partyKey === 'all' ? {} : { party_key: partyKey }),
          ...(region === 'all' ? {} : { region }),
          ...(entity === 'all' ? {} : { cloudeq_entity: entity }),
          day_start_utc: localDayStartUtc(),
        },
        abort,
      ),
    [period, partyKey, region, entity],
  );

  /* The options come from the response (D-81-8); while loading, keep the last selection visible. */
  const clients = state.kind === 'ready' ? state.response.clients : [];

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="analytics-overview-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      <section className="card analytics-section" aria-labelledby="analytics-overview-heading">
        <div className="analytics-head">
          <label>
            Period
            <select
              aria-label="Period"
              value={period}
              onChange={(event) => {
                setPeriod(event.target.value as AnalyticsPeriod);
              }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Client
            <select
              aria-label="Client"
              value={partyKey}
              onChange={(event) => {
                setPartyKey(event.target.value);
              }}
            >
              <option value="all">All clients</option>
              {clients.map((client) => (
                <option key={client.party_key} value={client.party_key}>
                  {client.display_name}
                </option>
              ))}
              {partyKey !== 'all' && !clients.some((client) => client.party_key === partyKey) ? (
                <option value={partyKey}>{partyKey}</option>
              ) : null}
            </select>
          </label>
          <label>
            Region
            <select
              aria-label="Region"
              value={region}
              onChange={(event) => {
                setRegion(event.target.value as 'all' | Region);
              }}
            >
              <option value="all">All regions</option>
              {REGIONS.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            cloudEQ entity
            <select
              aria-label="cloudEQ entity"
              value={entity}
              onChange={(event) => {
                setEntity(event.target.value);
              }}
            >
              <option value="all">All cloudEQ entities</option>
              {entities.map((row) => (
                <option key={row.entity_key} value={row.entity_key}>
                  {row.display_name}
                </option>
              ))}
            </select>
          </label>
          <p className="mut">Counts are your scope only.</p>
        </div>
        <p className="mut">{NO_MONEY_COPY}</p>

        {state.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading {PANEL}…
          </p>
        ) : null}

        {state.kind === 'problem' ? (
          <PanelProblem
            panel={PANEL}
            state={state}
            endpointPath={ENDPOINT_PATH}
            onRetry={reload}
            forbiddenCopy={ANALYTICS_FORBIDDEN_COPY}
          />
        ) : null}

        {state.kind === 'ready' ? (
          <>
            <p className="mut">
              {String(state.response.total_in_scope)} contracts in scope for the period · {String(state.response.total_today)}{' '}
              today
            </p>
            <section className="analytics-section" aria-labelledby="analytics-by-status">
              <h2 id="analytics-by-status">By pipeline state</h2>
              <div className="kpi-grid" role="group" aria-label="Contracts by pipeline state">
                {PROCESSING_STATUSES.map((status) => {
                  const tile = state.response.by_status[status];
                  return (
                    <StatTile
                      key={status}
                      title={STATUS_LABELS[status]}
                      n={tile.n}
                      n_today={tile.n_today}
                      tone={STATUS_TONES[status]}
                    />
                  );
                })}
              </div>
            </section>
            <section className="analytics-section" aria-labelledby="analytics-by-type">
              <h2 id="analytics-by-type">By contract type</h2>
              <div className="kpi-grid" role="group" aria-label="Contracts by type">
                {CONTRACT_TYPES.map((type) => {
                  const tile = state.response.by_type[type];
                  const provisional = state.response.by_type_provisional[type];
                  return (
                    <StatTile
                      key={type}
                      title={TYPE_LABELS[type]}
                      n={tile.n}
                      n_today={tile.n_today}
                      tone="up"
                      caption={`${provisional.toLocaleString()} provisional`}
                    />
                  );
                })}
                <StatTile
                  key="pending"
                  title="Type pending"
                  n={state.response.by_type_pending.n}
                  n_today={state.response.by_type_pending.n_today}
                  tone="up"
                  caption="no type yet — never counted as Other"
                />
              </div>
            </section>
          </>
        ) : null}
      </section>
    </>
  );
}
